import Vue from 'vue'

import { api } from '@/services'

import {
  getSignedUrl,
  uploadToS3,
  attachToResource,
} from '@/services/upload/uploadService'

const state = {
  appointmentId: null,
  appointmentFiles: [],
  appointmentTerms: [],
  appointmentOutputs: [],
  appointmentTermsSigned: [],
  appointmentFilesToDelete: [],
  isAppointmentFilesLoading: false,
  isAppointmentFilesDeleting: false,
  isAppointmentFilesUploading: false,
  isTermUploadSuccessful: false,
  hasTerm: false,
  isAttachmentUploadSuccessful: false,
  hasAttachment: false,
}

const getters = {
  appointmentFiles: (state) => state.appointmentFiles,
  appointmentTerms: (state) => state.appointmentTerms,
  appointmentOutputs: (state) => state.appointmentOutputs,
  appointmentTermsSigned: (state) => state.appointmentTermsSigned,
  appointmentFilesToDelete: (state) => state.appointmentFilesToDelete,
  isAppointmentFilesLoading: (state) => state.isAppointmentFilesLoading,
  isAppointmentFilesDeleting: (state) => state.isAppointmentFilesDeleting,
  isAppointmentFilesUploading: (state) => state.isAppointmentFilesUploading,
  isTermUploadSuccessful: (state) => state.isTermUploadSuccessful,
  hasTerm: (state) => state.hasTerm,
  isAttachmentUploadSuccessful: (state) => state.isAttachmentUploadSuccessful,
  hasAttachment: (state) => state.hasAttachment,
}

const actions = {
  async fetchAppointmentFiles({ commit, dispatch }, appointmentId) {
    commit('setAppointmentFilesLoading', true)
    commit('setAppointmentId', appointmentId)

    const files = []
    const terms = []
    const termsSigned = []

    try {
      const { data } = await api.get(`/web/appointment/${appointmentId}/files`)

      for await (const file of data.files) {
        if (file?.file_source === 'TERM') terms.push(file)
        else if (file?.file_source === 'SIGNED_TERM') termsSigned.push(file)
        else files.push(file)
      }

      commit('setAppointmentTerms', terms)
      commit('setAppointmentFiles', files)
      commit('setAppointmentOutputs', data.outputs)
      commit('setAppointmentTermsSigned', termsSigned)
    } catch ({ data }) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', data.errors[0].message)
        : dispatch('showErrorMessage', data.message)
    } finally {
      commit('setAppointmentFilesLoading', false)
    }
  },

  async attachAppointmentFiles({ commit, dispatch }, files) {
    commit('setIsAppointmentFilesUploading', true)

    for (const file of files) {
      try {
        const uploadUrl = `/web/appointment/${state.appointmentId}/fileUploadURL`
        const attachUrl = `/web/appointment/${state.appointmentId}/attachFile`

        if (file.name.length < 100 && file.type) {
          const { signedUrl, external_filename } = await getSignedUrl(
            {
              filename: file.name,
              filetype: file.type,
              folder_name: 'files',
            },
            uploadUrl
          )

          await uploadToS3(signedUrl, file)
          await attachToResource(
            { external_filename, filename: file.name },
            attachUrl
          )
        } else {
          commit('setIsAppointmentFilesUploading', false)
          dispatch(
            'showErrorMessage',
            Vue.i18n.translate('upload.error.nameOrExtension')
          )

          return
        }
      } catch ({ data }) {
        commit('setIsAppointmentFilesUploading', false)

        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
        return
      }
    }

    await dispatch('fetchAppointmentFiles', state.appointmentId)
    await dispatch(
      'showSuccessMessage',
      Vue.i18n.translate('upload.success.uploaded')
    )

    commit('setIsAppointmentFilesUploading', false)
  },

  async deleteAppointmentFile({ commit, dispatch }) {
    commit('setIsAppointmentFilesDeleting', true)

    try {
      await api.post(`/web/appointment/${state.appointmentId}/deleteFiles`, {
        file_ids: state.appointmentFilesToDelete.map((file) =>
          file?.id ? file?.id : file
        ),
      })

      await dispatch('fetchAppointmentFiles', state.appointmentId)
      await dispatch(
        'showSuccessMessage',
        Vue.i18n.translate('upload.success.excluded')
      )

      commit('closeAllModals')
      commit('setAppointmentFilesToDelete', [])
    } catch ({ data }) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', data.errors[0].message)
        : dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsAppointmentFilesDeleting', false)
    }
  },

  async attachTerminationFile({ commit, dispatch, rootState }, {files, isAdmin = false}) {
    commit('setIsTermUploadSuccessful', false)
    commit('setIsAppointmentFilesUploading', true)

    // add old name to backend prevent a
    // super population of files for the appointment
    const oldFileName = rootState?.appointmentForm?.appointmentFields?.term?.filename
    const realEstateId = rootState?.appointment?.appointment?.real_estate?.id

    for (const file of files) {
      try {
        const uploadUrl = `${isAdmin ? 'admin' : 'web'}/appointment/termUploadUrl`

        if (file.name.length < 100 && file.type) {
          const { signedUrl, external_filename } = await getSignedUrl(
            {
              filename: file.name,
              filetype: file.type,
              old_file_external_name: oldFileName ? oldFileName : file.name,
              real_estate_id: isAdmin ? realEstateId : null
            },
            uploadUrl
          )

          await uploadToS3(signedUrl, file)

          const term = {
            external_filename,
            filename: file.name,
            external_url: signedUrl.substring(0, signedUrl.indexOf('?')),
          }

          commit('setAppointmentFields', { term })
          commit('setIsTermUploadSuccessful', true)
          commit('setIsAppointmentFilesUploading', false)
          commit('setHasTerm', true)
        } else {
          commit('setIsAppointmentFilesUploading', false)
          dispatch(
            'showErrorMessage',
            Vue.i18n.translate('upload.error.nameOrExtension')
          )

          return
        }
      } catch (error) {
        dispatch('showErrorMessage', error.toString())
      }
    }
  },

  async attachAppointmentAttachment({ commit, dispatch, rootState }, { files, isAdmin = false }) {
    commit('setIsAttachmentUploadSuccessful', false)
    commit('setIsAppointmentFilesUploading', true)

    const realEstateId = rootState?.appointmentForm?.appointmentFields?.real_estate_id

    for (const file of files) {
      try {
        const uploadUrl = `${isAdmin ? 'admin' : 'web'}/appointment/attachmentUploadUrl`

        if (file.name.length < 100 && file.type) {
          const signedUrlData = {
            filename: file.name,
            filetype: file.type,
          }

          if (isAdmin && realEstateId) {
            signedUrlData.real_estate_id = realEstateId
          }

          const { signedUrl, external_filename } = await getSignedUrl(
            signedUrlData,
            uploadUrl
          )

          await uploadToS3(signedUrl, file)

          const attachment = {
            external_filename,
            filename: file.name,
            external_url: signedUrl.substring(0, signedUrl.indexOf('?')),
          }

          commit('setAppointmentFields', { attachment })
          commit('setIsAttachmentUploadSuccessful', true)
          commit('setIsAppointmentFilesUploading', false)
          commit('setHasAttachment', true)
        } else {
          commit('setIsAppointmentFilesUploading', false)
          dispatch(
            'showErrorMessage',
            Vue.i18n.translate('upload.error.nameOrExtension')
          )

          return
        }
      } catch (error) {
        commit('setIsAppointmentFilesUploading', false)
        dispatch('showErrorMessage', error.toString())
      }
    }
  },
}

const mutations = {
  setAppointmentId(state, id) {
    state.appointmentId = id
  },

  setAppointmentFilesLoading(state, status) {
    state.isAppointmentFilesLoading = status
  },

  setAppointmentOutputs: (state, appointmentOutputs) => {
    state.appointmentOutputs = appointmentOutputs
  },

  setIsAppointmentFilesUploading(state, status) {
    state.isAppointmentFilesUploading = status
  },

  setIsAppointmentFilesDeleting(state, status) {
    state.isAppointmentFilesDeleting = status
  },

  setAppointmentFilesToDelete(state, files) {
    state.appointmentFilesToDelete = files
  },

  setAppointmentFiles(state, files) {
    state.appointmentFiles = files
  },

  setAppointmentTerms(state, terms) {
    state.appointmentTerms = terms
  },

  setAppointmentTermsSigned(state, terms) {
    state.appointmentTermsSigned = terms
  },

  setIsTermUploadSuccessful(state, status) {
    state.isTermUploadSuccessful = status
  },

  setHasTerm(state, status) {
    state.hasTerm = status
  },

  setIsAttachmentUploadSuccessful(state, status) {
    state.isAttachmentUploadSuccessful = status
  },

  setHasAttachment(state, status) {
    state.hasAttachment = status
  },
}

export default {
  state,
  mutations,
  getters,
  actions,
}
